/**
 * Browser telemetry adapter
 * Supports New Relic Browser agent or beacon-based custom telemetry
 */
import type {
  Telemetry,
  TelemetryConfig,
  Transaction,
  TransactionContext,
  MetricAttributes,
} from '../telemetry/types.js';

export interface BrowserTelemetryConfig extends TelemetryConfig {
  /** Beacon endpoint for custom telemetry (if not using New Relic Browser) */
  beaconUrl?: string;
  /**
   * New Relic Browser application ID (from NR UI → Browser → Settings).
   * When provided alongside licenseKey and window.newrelic is not yet present,
   * the NR SPA loader script is injected dynamically at startup.
   */
  applicationId?: string;
  /**
   * New Relic Browser-specific license key (different from the APM/infra key).
   * Injected at container startup via docker-entrypoint.sh → window.__ENV__.
   */
  licenseKey?: string;
}

// New Relic Browser agent global type
declare global {
  interface Window {
    newrelic?: {
      setCustomAttribute(name: string, value: string | number | boolean): void;
      addPageAction(name: string, attributes?: Record<string, unknown>): void;
      noticeError(error: Error, customAttributes?: Record<string, unknown>): void;
      interaction(): {
        setName(name: string): BrowserInteraction;
        setAttribute(key: string, value: string | number | boolean): BrowserInteraction;
        save(): BrowserInteraction;
        end(): void;
      };
      setPageViewName(name: string): void;
      setErrorHandler(handler: (error: Error) => boolean): void;
      finished(time?: number): void;
    };
  }
}

interface BrowserInteraction {
  setName(name: string): BrowserInteraction;
  setAttribute(key: string, value: string | number | boolean): BrowserInteraction;
  save(): BrowserInteraction;
  end(): void;
}

class BrowserTransaction implements Transaction {
  private readonly interaction: BrowserInteraction | null;

  constructor(context: TransactionContext) {
    if (window.newrelic) {
      this.interaction = window.newrelic.interaction();
      this.interaction.setName(context.name);

      if (context.attributes) {
        for (const [key, value] of Object.entries(context.attributes)) {
          this.interaction.setAttribute(key, value);
        }
      }
      this.interaction.save();
    } else {
      this.interaction = null;
    }
  }

  startSegment<T>(_name: string, _record: boolean, handler: () => T | Promise<T>): T | Promise<T> {
    // Browser agent doesn't have segment concept, just run the handler
    return handler();
  }

  addAttribute(key: string, value: string | number | boolean): void {
    this.interaction?.setAttribute(key, value);
  }

  end(): void {
    this.interaction?.end();
  }

  ignore(): void {
    // No-op in browser
  }
}

class NewRelicBrowserTelemetry implements Telemetry {
  constructor(private readonly config: BrowserTelemetryConfig) {}

  startTransaction(context: TransactionContext): Transaction {
    return new BrowserTransaction(context);
  }

  getCurrentTransaction(): Transaction | null {
    // Browser agent doesn't expose current transaction
    return null;
  }

  recordMetric(name: string, value: number, attributes?: MetricAttributes): void {
    // Use PageAction for custom metrics in browser
    window.newrelic?.addPageAction(`metric:${name}`, {
      value,
      ...attributes,
    });
  }

  recordEvent(eventType: string, attributes: MetricAttributes): void {
    window.newrelic?.addPageAction(eventType, attributes);
  }

  addCustomAttributes(attributes: MetricAttributes): void {
    if (!window.newrelic) return;
    for (const [key, value] of Object.entries(attributes)) {
      window.newrelic.setCustomAttribute(key, value);
    }
  }

  noticeError(error: Error, attributes?: MetricAttributes): void {
    window.newrelic?.noticeError(error, attributes);
  }

  async shutdown(): Promise<void> {
    window.newrelic?.finished();
  }
}

/**
 * Beacon-based telemetry for when New Relic Browser is not available
 */
class BeaconTelemetry implements Telemetry {
  constructor(private readonly beaconUrl: string) {}

  private send(type: string, data: Record<string, unknown>): void {
    const payload = {
      type,
      timestamp: Date.now(),
      url: window.location.href,
      userAgent: navigator.userAgent,
      ...data,
    };

    if (navigator.sendBeacon) {
      navigator.sendBeacon(this.beaconUrl, JSON.stringify(payload));
    } else {
      fetch(this.beaconUrl, {
        method: 'POST',
        body: JSON.stringify(payload),
        headers: { 'Content-Type': 'application/json' },
        keepalive: true,
      }).catch(() => { /* ignore beacon errors */ });
    }
  }

  startTransaction(context: TransactionContext): Transaction {
    this.send('transaction:start', { name: context.name, attributes: context.attributes });
    return {
      startSegment: <T>(_name: string, _record: boolean, handler: () => T | Promise<T>) => handler(),
      addAttribute: () => { /* noop */ },
      end: () => this.send('transaction:end', { name: context.name }),
      ignore: () => { /* noop */ },
    };
  }

  getCurrentTransaction(): Transaction | null {
    return null;
  }

  recordMetric(name: string, value: number, attributes?: MetricAttributes): void {
    this.send('metric', { name, value, attributes });
  }

  recordEvent(eventType: string, attributes: MetricAttributes): void {
    this.send('event', { eventType, attributes });
  }

  addCustomAttributes(attributes: MetricAttributes): void {
    this.send('attributes', { attributes });
  }

  noticeError(error: Error, attributes?: MetricAttributes): void {
    this.send('error', {
      message: error.message,
      stack: error.stack,
      name: error.name,
      attributes,
    });
  }

  async shutdown(): Promise<void> { /* noop */ }
}

/**
 * No-op telemetry for when telemetry is disabled
 */
class NoopBrowserTelemetry implements Telemetry {
  startTransaction(): Transaction {
    return {
      startSegment: <T>(_name: string, _record: boolean, handler: () => T | Promise<T>) => handler(),
      addAttribute: () => { /* noop */ },
      end: () => { /* noop */ },
      ignore: () => { /* noop */ },
    };
  }

  getCurrentTransaction(): Transaction | null {
    return null;
  }

  recordMetric(): void { /* noop */ }
  recordEvent(): void { /* noop */ }
  addCustomAttributes(): void { /* noop */ }
  noticeError(): void { /* noop */ }
  async shutdown(): Promise<void> { /* noop */ }
}

/**
 * Inject the New Relic Browser SPA loader and NREUM config block into <head>
 * when the agent hasn't been embedded statically in index.html.
 * This is the standard approach for containerised SPAs where the browser key
 * is a runtime secret (not available at build time).
 */
function injectNewRelicBrowserAgent(applicationId: string, licenseKey: string): void {
  if (typeof document === 'undefined') return;

  // NREUM config must be defined before the loader script executes
  const configScript = document.createElement('script');
  configScript.text = [
    'window.NREUM||(NREUM={});',
    'NREUM.info={',
    `  beacon:"bam.nr-data.net",`,
    `  licenseKey:${JSON.stringify(licenseKey)},`,
    `  applicationID:${JSON.stringify(applicationId)},`,
    '  sa:1',
    '};',
  ].join('\n');
  document.head.insertBefore(configScript, document.head.firstChild);

  const loaderScript = document.createElement('script');
  loaderScript.src = 'https://js-agent.newrelic.com/nr-loader-spa-current.min.js';
  loaderScript.async = false; // must execute in order relative to config block
  document.head.appendChild(loaderScript);
}

/**
 * Initialize browser telemetry
 */
export function initializeBrowserTelemetry(config: BrowserTelemetryConfig): Telemetry {
  if (!config.enabled) {
    return new NoopBrowserTelemetry();
  }

  // If the NR Browser agent is not already on the page but credentials are
  // available, inject the loader now so window.newrelic is set before the
  // first page interaction reaches the Vue app.
  if (!window.newrelic && config.applicationId && config.licenseKey) {
    injectNewRelicBrowserAgent(config.applicationId, config.licenseKey);
  }

  // Check if New Relic Browser agent is loaded (either pre-existing or just injected)
  if (window.newrelic) {
    return new NewRelicBrowserTelemetry(config);
  }

  // Fall back to beacon-based telemetry
  if (config.beaconUrl) {
    return new BeaconTelemetry(config.beaconUrl);
  }

  // No telemetry available
  return new NoopBrowserTelemetry();
}

export { NoopBrowserTelemetry };
